import { useState, useEffect, useCallback } from 'react';
import sanitizeText from '@/utils/sanitizeText';
import {
  FaGlobe,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSync,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaToggleOn,
  FaToggleOff,
  FaSave,
  FaTimes,
  FaServer,
  FaShieldAlt,
  FaRocket,
  FaHistory,
  FaClock,
  FaCheck,
  FaSpinner,
  FaBan,
  FaInfoCircle,
  FaChevronDown,
  FaChevronUp,
  FaListUl,
  FaPlay
} from 'react-icons/fa';
import './MultiRouterDeployment.scss';
import * as Sentry from '@sentry/react';

/**
 * MultiRouterDeployment Component
 *
 * Full-featured multi-router configuration deployment interface.
 * Three integrated sections:
 *   1. Unified Rules — Create/manage global firewall rules synced to all routers
 *   2. Bulk Deploy — Build custom commands, select target routers, execute
 *   3. History — View past deployments and per-router results
 */
const MultiRouterDeployment = ({ apiFetch }) => {
  // ===== Section navigation =====
  const [activeSection, setActiveSection] = useState('rules');

  // ===== Shared state =====
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // ===== Unified Rules state =====
  const [unifiedRules, setUnifiedRules] = useState([]);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({});

  // ===== Bulk Deploy state =====
  const [routers, setRouters] = useState([]);
  const [selectedRouterIds, setSelectedRouterIds] = useState([]);
  const [bulkForm, setBulkForm] = useState({
    operationName: '',
    description: '',
    operationType: 'ConfigChange',
    commandScript: '',
    useSafeMode: true,
    safeModeTimeoutSeconds: 300,
  });
  const [deploying, setDeploying] = useState(false);

  // ===== History state =====
  const [operations, setOperations] = useState([]);
  const [expandedOpId, setExpandedOpId] = useState(null);
  const [expandedOpDetail, setExpandedOpDetail] = useState(null);

  // ===== Auto-dismiss success =====
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // ===== Data loaders =====
  const loadRouters = useCallback(async () => {
    try {
      const response = await apiFetch('/api/RouterConfig');
      const data = await response.json();
      if (data.success) setRouters(data.data || []);
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error loading routers:', err);
    }
  }, [apiFetch]);

  const loadUnifiedRules = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiFetch('/api/RouterConfig/firewall/rules');
      const data = await response.json();
      if (data.success) setUnifiedRules(data.data || []);
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error loading unified rules:', err);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  const loadOperations = useCallback(async () => {
    try {
      const response = await apiFetch('/api/RouterConfig/bulk-operations');
      const data = await response.json();
      if (data.success) setOperations(data.data || []);
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error loading operations:', err);
    }
  }, [apiFetch]);

  // ===== Initial load =====
  useEffect(() => {
    (async () => {
      await loadRouters();
      await loadUnifiedRules();
      await loadOperations();
    })();
  }, [loadRouters, loadUnifiedRules, loadOperations]);

  const loadOperationDetail = useCallback(async (opId) => {
    try {
      const response = await apiFetch(`/api/RouterConfig/bulk-operations/${opId}`);
      const data = await response.json();
      if (data.success) setExpandedOpDetail(data.data);
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error loading operation detail:', err);
    }
  }, [apiFetch]);

  // ===== Unified Rules CRUD =====
  const defaultRuleForm = {
    ruleName: '',
    description: '',
    ruleType: 'Custom',
    chain: 'forward',
    action: 'drop',
    sourceAddress: '',
    destinationAddress: '',
    protocol: '',
    sourcePort: '',
    destinationPort: '',
    isEnabled: true,
    priority: 100,
    reason: '',
  };

  const openCreateRuleModal = () => {
    setEditingRule(null);
    setRuleForm({ ...defaultRuleForm });
    setShowRuleModal(true);
  };

  const openEditRuleModal = (rule) => {
    setEditingRule(rule);
    setRuleForm({
      ruleName: rule.ruleName || '',
      description: rule.description || '',
      ruleType: rule.ruleType || 'Custom',
      chain: rule.chain || 'forward',
      action: rule.action || 'drop',
      sourceAddress: rule.sourceAddress || '',
      destinationAddress: rule.destinationAddress || '',
      protocol: rule.protocol || '',
      sourcePort: rule.sourcePort || '',
      destinationPort: rule.destinationPort || '',
      isEnabled: rule.isEnabled !== false,
      priority: rule.priority || 100,
      reason: rule.reason || '',
    });
    setShowRuleModal(true);
  };

  const saveRule = async () => {
    if (!ruleForm.ruleName) { setError('Rule name is required'); return; }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        ruleName: ruleForm.ruleName,
        description: ruleForm.description,
        ruleType: ruleForm.ruleType,
        chain: ruleForm.chain,
        action: ruleForm.action,
        sourceAddress: ruleForm.sourceAddress || null,
        destinationAddress: ruleForm.destinationAddress || null,
        protocol: ruleForm.protocol || null,
        sourcePort: ruleForm.sourcePort || null,
        destinationPort: ruleForm.destinationPort || null,
        isEnabled: ruleForm.isEnabled,
        priority: ruleForm.priority,
        reason: ruleForm.reason || null,
        sourceSystem: 'Manual',
      };

      if (editingRule) {
        // Update is not yet implemented on backend for unified rules — inform user
        setError('Editing unified rules is not yet supported. Delete and re-create the rule.');
        setLoading(false);
        return;
      }

      const response = await apiFetch('/api/RouterConfig/firewall/rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Rule created and deployment started to all routers');
        setShowRuleModal(false);
        await loadUnifiedRules();
      } else {
        setError(data.error || 'Failed to create rule');
      }
    } catch (err) {
        Sentry.captureException(err);
      setError(err.message || 'Failed to create rule');
    } finally {
      setLoading(false);
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Delete unified rule "${rule.ruleName}"?\nThis will NOT remove the rule from routers that already have it.`)) return;
    setLoading(true);
    try {
      const response = await apiFetch(`/api/RouterConfig/firewall/rules/${rule.id}`, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        setSuccess('Rule deleted');
        await loadUnifiedRules();
      } else {
        setError(data.error || 'Failed to delete rule');
      }
    } catch (err) {
        Sentry.captureException(err);
      setError(err.message || 'Failed to delete rule');
    } finally {
      setLoading(false);
    }
  };

  // ===== Bulk Deploy =====
  const toggleRouter = (id) => {
    setSelectedRouterIds(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  const selectAllRouters = () => {
    const enabledIds = routers.filter(r => r.isEnabled).map(r => r.id);
    setSelectedRouterIds(enabledIds);
  };

  const deselectAllRouters = () => setSelectedRouterIds([]);

  const executeBulkDeploy = async () => {
    if (!bulkForm.operationName) { setError('Operation name is required'); return; }
    if (!bulkForm.commandScript.trim()) { setError('Command script is required'); return; }
    if (selectedRouterIds.length === 0) { setError('Select at least one target router'); return; }

    if (!window.confirm(`Deploy "${bulkForm.operationName}" to ${selectedRouterIds.length} router(s)?\n\nSafe Mode: ${bulkForm.useSafeMode ? 'ON' : 'OFF'}`)) return;

    setDeploying(true);
    setError(null);
    try {
      const payload = {
        operationName: bulkForm.operationName,
        description: bulkForm.description,
        operationType: bulkForm.operationType,
        commandScript: bulkForm.commandScript,
        targetRouterIds: selectedRouterIds,
        useSafeMode: bulkForm.useSafeMode,
        safeModeTimeoutSeconds: bulkForm.safeModeTimeoutSeconds,
        validateWithLLM: false,
      };

      const response = await apiFetch('/api/RouterConfig/bulk-operations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(`Deployment "${bulkForm.operationName}" started — ${selectedRouterIds.length} router(s) targeted`);
        setBulkForm({ operationName: '', description: '', operationType: 'ConfigChange', commandScript: '', useSafeMode: true, safeModeTimeoutSeconds: 300 });
        setSelectedRouterIds([]);
        // Switch to history and reload after short delay
        setTimeout(() => { setActiveSection('history'); loadOperations(); }, 1500);
      } else {
        setError(data.error || 'Failed to start deployment');
      }
    } catch (err) {
        Sentry.captureException(err);
      setError(err.message || 'Failed to start deployment');
    } finally {
      setDeploying(false);
    }
  };

  // ===== History expansion =====
  const toggleOperationExpand = (opId) => {
    if (expandedOpId === opId) {
      setExpandedOpId(null);
      setExpandedOpDetail(null);
    } else {
      setExpandedOpId(opId);
      loadOperationDetail(opId);
    }
  };

  // ===== Status helpers =====
  const getStatusIcon = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'success' || s === 'completed' || s === 'synced') return <FaCheckCircle color="#4caf50" />;
    if (s === 'failed') return <FaTimesCircle color="#f44336" />;
    if (s === 'inprogress' || s === 'pending') return <FaSpinner className="spinning" color="#ff9800" />;
    if (s === 'partialsuccess' || s === 'partialsync') return <FaExclamationTriangle color="#ff9800" />;
    if (s === 'skipped') return <FaBan color="#999" />;
    return <FaInfoCircle color="#999" />;
  };

  const getStatusClass = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'success' || s === 'completed' || s === 'synced') return 'status-success';
    if (s === 'failed') return 'status-failed';
    if (s === 'inprogress' || s === 'pending') return 'status-pending';
    if (s === 'partialsuccess' || s === 'partialsync') return 'status-partial';
    return 'status-unknown';
  };

  const getActionColor = (action) => {
    if (!action) return '#6c757d';
    const a = action.toLowerCase();
    if (a === 'accept') return '#4caf50';
    if (a === 'drop' || a === 'reject') return '#f44336';
    if (a === 'masquerade') return '#9c27b0';
    return '#2196f3';
  };

  // ===== Section tabs =====
  const sections = [
    { key: 'rules', label: 'Unified Rules', icon: <FaShieldAlt />, count: unifiedRules.length },
    { key: 'deploy', label: 'Bulk Deploy', icon: <FaRocket /> },
    { key: 'history', label: 'History', icon: <FaHistory />, count: operations.length },
  ];

  // ===== Render: Unified Rules =====
  const renderUnifiedRules = () => (
    <div className="section-panel">
      <div className="section-header">
        <div className="section-info">
          <h4><FaShieldAlt /> Unified Firewall Rules</h4>
          <p>Rules defined here are automatically deployed to <strong>all enabled routers</strong>. Use this for global security policies.</p>
        </div>
        <div className="section-actions">
          <button onClick={loadUnifiedRules} className="btn-icon-text" disabled={loading} title="Refresh"><FaSync className={loading ? 'spinning' : ''} /> Refresh</button>
          <button onClick={openCreateRuleModal} className="btn-primary-sm"><FaPlus /> Create Rule</button>
        </div>
      </div>

      {unifiedRules.length === 0 ? (
        <div className="empty-state">
          <FaShieldAlt size={40} color="#ccc" />
          <p>No unified rules yet</p>
          <span>Create a rule to deploy it to all routers automatically.</span>
          <button onClick={openCreateRuleModal} className="btn-primary-sm"><FaPlus /> Create First Rule</button>
        </div>
      ) : (
        <div className="rules-grid">
          {unifiedRules.map(rule => (
            <div key={rule.id} className={`rule-card ${!rule.isEnabled ? 'disabled-card' : ''}`}>
              <div className="rule-card-header">
                <div className="rule-name-row">
                  <span className={`rule-type-badge ${rule.ruleType?.toLowerCase() || 'custom'}`}>{rule.ruleType || 'Custom'}</span>
                  <strong>{sanitizeText(rule.ruleName)}</strong>
                </div>
                <div className="rule-card-actions">
                  <button onClick={() => openEditRuleModal(rule)} className="btn-icon-sm" title="Edit"><FaEdit /></button>
                  <button onClick={() => deleteRule(rule)} className="btn-icon-sm btn-danger-sm" title="Delete"><FaTrash /></button>
                </div>
              </div>
              {rule.description && <p className="rule-desc">{sanitizeText(rule.description)}</p>}
              <div className="rule-meta">
                <span className="meta-tag"><strong>Chain:</strong> {rule.chain}</span>
                <span className="meta-tag" style={{ backgroundColor: getActionColor(rule.action), color: '#fff' }}>{rule.action}</span>
                {rule.protocol && <span className="meta-tag">{rule.protocol}</span>}
                {rule.sourceAddress && <span className="meta-tag">Src: {rule.sourceAddress}</span>}
                {rule.destinationAddress && <span className="meta-tag">Dst: {rule.destinationAddress}</span>}
                {rule.sourcePort && <span className="meta-tag">SPort: {rule.sourcePort}</span>}
                {rule.destinationPort && <span className="meta-tag">DPort: {rule.destinationPort}</span>}
              </div>
              <div className="rule-footer">
                <span className="priority-badge">Priority: {rule.priority}</span>
                <span className={`sync-badge ${getStatusClass(rule.syncStatus)}`}>
                  {getStatusIcon(rule.syncStatus)} {rule.syncStatus || 'Pending'}
                </span>
                {!rule.isEnabled && <span className="disabled-badge"><FaToggleOff /> Disabled</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ===== Render: Bulk Deploy =====
  const renderBulkDeploy = () => {
    const enabledRouters = routers.filter(r => r.isEnabled);
    const disabledRouters = routers.filter(r => !r.isEnabled);

    return (
      <div className="section-panel">
        <div className="section-header">
          <div className="section-info">
            <h4><FaRocket /> Bulk Deploy</h4>
            <p>Build a RouterOS command script, select target routers, and execute. Safe Mode protects against misconfigurations.</p>
          </div>
        </div>

        <div className="deploy-layout">
          {/* Left: Command Builder */}
          <div className="deploy-command-panel">
            <h5>Command Configuration</h5>
            <div className="form-group-md">
              <label>Operation Name *</label>
              <input type="text" value={bulkForm.operationName} onChange={(e) => setBulkForm({ ...bulkForm, operationName: e.target.value })} placeholder="e.g., Block known scanner IPs" />
            </div>
            <div className="form-group-md">
              <label>Description</label>
              <input type="text" value={bulkForm.description} onChange={(e) => setBulkForm({ ...bulkForm, description: e.target.value })} placeholder="Optional description" />
            </div>
            <div className="form-row-2">
              <div className="form-group-md">
                <label>Operation Type</label>
                <select value={bulkForm.operationType} onChange={(e) => setBulkForm({ ...bulkForm, operationType: e.target.value })}>
                  <option value="ConfigChange">Config Change</option>
                  <option value="FirewallRule">Firewall Rule</option>
                  <option value="ScriptExecution">Script Execution</option>
                </select>
              </div>
              <div className="form-group-md">
                <label>Safe Mode Timeout</label>
                <select value={bulkForm.safeModeTimeoutSeconds} onChange={(e) => setBulkForm({ ...bulkForm, safeModeTimeoutSeconds: parseInt(e.target.value) })}>
                  <option value={120}>2 minutes</option>
                  <option value={300}>5 minutes</option>
                  <option value={600}>10 minutes</option>
                </select>
              </div>
            </div>
            <div className="form-group-md">
              <label>RouterOS Command Script *</label>
              <textarea
                value={bulkForm.commandScript}
                onChange={(e) => setBulkForm({ ...bulkForm, commandScript: e.target.value })}
                placeholder={`Enter RouterOS commands, one per line.\nExample:\n/ip firewall filter add chain=forward action=drop src-address=198.51.100.0/24 comment="Block bad network"`}
                rows={8}
              />
            </div>
            <div className="safe-mode-toggle">
              <button className={`toggle-btn ${bulkForm.useSafeMode ? 'active' : ''}`} onClick={() => setBulkForm({ ...bulkForm, useSafeMode: !bulkForm.useSafeMode })}>
                {bulkForm.useSafeMode ? <FaToggleOn size={20} /> : <FaToggleOff size={20} />}
                Safe Mode {bulkForm.useSafeMode ? 'ON' : 'OFF'}
              </button>
              <span className="safe-mode-hint">
                {bulkForm.useSafeMode
                  ? 'Changes will auto-revert if the router loses connectivity'
                  : 'Changes will be applied without safety net — use with caution'}
              </span>
            </div>
          </div>

          {/* Right: Router Selector */}
          <div className="deploy-router-panel">
            <div className="router-selector-header">
              <h5><FaServer /> Target Routers</h5>
              <div className="select-actions">
                <button onClick={selectAllRouters} className="btn-link-sm">Select All ({enabledRouters.length})</button>
                <button onClick={deselectAllRouters} className="btn-link-sm">Clear</button>
              </div>
            </div>
            <div className="selected-count">
              <strong>{selectedRouterIds.length}</strong> of {enabledRouters.length} router(s) selected
            </div>
            <div className="router-list">
              {enabledRouters.map(r => (
                <label key={r.id} className={`router-item ${selectedRouterIds.includes(r.id) ? 'selected' : ''}`}>
                  <input type="checkbox" checked={selectedRouterIds.includes(r.id)} onChange={() => toggleRouter(r.id)} />
                  <div className="router-item-info">
                    <span className="router-item-name">{sanitizeText(r.displayName || r.hostname || r.ipAddress)}</span>
                    <span className="router-item-ip">{r.ipAddress}</span>
                  </div>
                </label>
              ))}
              {disabledRouters.length > 0 && (
                <div className="disabled-routers-note">
                  <FaInfoCircle /> {disabledRouters.length} disabled router(s) not shown
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Deploy Button */}
        <div className="deploy-action-bar">
          <button onClick={executeBulkDeploy} className="btn-deploy" disabled={deploying || selectedRouterIds.length === 0 || !bulkForm.commandScript.trim() || !bulkForm.operationName}>
            {deploying ? <><FaSpinner className="spinning" /> Deploying...</> : <><FaRocket /> Deploy to {selectedRouterIds.length} Router(s)</>}
          </button>
        </div>
      </div>
    );
  };

  // ===== Render: History =====
  const renderHistory = () => (
    <div className="section-panel">
      <div className="section-header">
        <div className="section-info">
          <h4><FaHistory /> Deployment History</h4>
          <p>All bulk operations and their per-router results.</p>
        </div>
        <button onClick={loadOperations} className="btn-icon-text" disabled={loading}><FaSync className={loading ? 'spinning' : ''} /> Refresh</button>
      </div>

      {operations.length === 0 ? (
        <div className="empty-state">
          <FaHistory size={40} color="#ccc" />
          <p>No deployments yet</p>
          <span>Deployments created from &quot;Bulk Deploy&quot; or &quot;Unified Rules&quot; will appear here.</span>
        </div>
      ) : (
        <div className="history-list">
          {operations.map(op => {
            const isExpanded = expandedOpId === op.id;
            return (
              <div key={op.id} className={`history-card ${getStatusClass(op.status)}`}>
                <div className="history-card-header" onClick={() => toggleOperationExpand(op.id)}>
                  <div className="history-title-row">
                    {getStatusIcon(op.status)}
                    <strong>{sanitizeText(op.operationName)}</strong>
                    <span className={`status-pill ${getStatusClass(op.status)}`}>{op.status}</span>
                  </div>
                  <div className="history-meta">
                    <span><FaClock /> {new Date(op.createdDate).toLocaleString()}</span>
                    <span><FaServer /> {op.totalRouters} router(s)</span>
                    <span className="success-count"><FaCheck /> {op.successCount}</span>
                    {op.failureCount > 0 && <span className="failure-count"><FaTimesCircle /> {op.failureCount}</span>}
                    <span className="expand-icon">{isExpanded ? <FaChevronUp /> : <FaChevronDown />}</span>
                  </div>
                </div>

                {isExpanded && (
                  <div className="history-detail">
                    {op.description && <p className="op-desc">{sanitizeText(op.description)}</p>}
                    <div className="op-command-preview">
                      <label>Command</label>
                      <pre>{sanitizeText(op.commandScript)}</pre>
                    </div>
                    <div className="op-settings">
                      <span>Type: {op.operationType}</span>
                      <span>Safe Mode: {op.useSafeMode ? 'Yes' : 'No'}</span>
                      {op.startedDate && <span>Started: {new Date(op.startedDate).toLocaleString()}</span>}
                      {op.completedDate && <span>Completed: {new Date(op.completedDate).toLocaleString()}</span>}
                    </div>

                    {/* Per-router results */}
                    {expandedOpDetail?.items && expandedOpDetail.items.length > 0 ? (
                      <table className="results-table">
                        <thead>
                          <tr>
                            <th>Router</th>
                            <th>Status</th>
                            <th>Duration</th>
                            <th>Message</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expandedOpDetail.items.map(item => (
                            <tr key={item.id} className={getStatusClass(item.status)}>
                              <td>{sanitizeText(item.routerConfig?.displayName || item.routerConfig?.ipAddress || `Router #${item.routerConfigId}`)}</td>
                              <td><span className={`status-pill-sm ${getStatusClass(item.status)}`}>{getStatusIcon(item.status)} {item.status}</span></td>
                              <td>{item.durationMs != null ? `${item.durationMs}ms` : '-'}</td>
                              <td className="msg-cell">{item.errorMessage ? <span className="error-text">{sanitizeText(item.errorMessage)}</span> : (item.output ? sanitizeText(item.output).substring(0, 120) : '-')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : expandedOpDetail ? (
                      <p className="no-items-note">No per-router details available.</p>
                    ) : (
                      <div className="loading-detail"><FaSpinner className="spinning" /> Loading details...</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ===== Render: Rule Modal =====
  const renderRuleModal = () => {
    if (!showRuleModal) return null;
    const f = ruleForm;
    const u = (key, val) => setRuleForm({ ...f, [key]: val });

    return (
      <div className="modal-overlay" onClick={() => setShowRuleModal(false)}>
        <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>{editingRule ? <><FaEdit /> Edit Unified Rule</> : <><FaPlus /> Create Unified Rule</>}</h3>
            <button onClick={() => setShowRuleModal(false)} className="btn-close"><FaTimes /></button>
          </div>
          <div className="modal-body">
            <div className="form-grid-2">
              <div className="form-group-md full-width-md">
                <label>Rule Name *</label>
                <input type="text" value={f.ruleName} onChange={(e) => u('ruleName', e.target.value)} placeholder="e.g., Block known scanner network" />
              </div>
              <div className="form-group-md full-width-md">
                <label>Description</label>
                <input type="text" value={f.description} onChange={(e) => u('description', e.target.value)} placeholder="Optional description" />
              </div>
              <div className="form-group-md">
                <label>Rule Type</label>
                <select value={f.ruleType} onChange={(e) => u('ruleType', e.target.value)}>
                  <option value="Blacklist">Blacklist</option>
                  <option value="Whitelist">Whitelist</option>
                  <option value="Custom">Custom</option>
                </select>
              </div>
              <div className="form-group-md">
                <label>Chain *</label>
                <select value={f.chain} onChange={(e) => u('chain', e.target.value)}>
                  <option value="input">input</option>
                  <option value="forward">forward</option>
                  <option value="output">output</option>
                </select>
              </div>
              <div className="form-group-md">
                <label>Action *</label>
                <select value={f.action} onChange={(e) => u('action', e.target.value)}>
                  <option value="drop">drop</option>
                  <option value="reject">reject</option>
                  <option value="accept">accept</option>
                  <option value="tarpit">tarpit</option>
                </select>
              </div>
              <div className="form-group-md">
                <label>Protocol</label>
                <select value={f.protocol} onChange={(e) => u('protocol', e.target.value)}>
                  <option value="">Any</option>
                  <option value="tcp">TCP</option>
                  <option value="udp">UDP</option>
                  <option value="icmp">ICMP</option>
                  <option value="gre">GRE</option>
                </select>
              </div>
              <div className="form-group-md">
                <label>Source Address</label>
                <input type="text" value={f.sourceAddress} onChange={(e) => u('sourceAddress', e.target.value)} placeholder="IP or CIDR" />
              </div>
              <div className="form-group-md">
                <label>Destination Address</label>
                <input type="text" value={f.destinationAddress} onChange={(e) => u('destinationAddress', e.target.value)} placeholder="IP or CIDR" />
              </div>
              <div className="form-group-md">
                <label>Source Port</label>
                <input type="text" value={f.sourcePort} onChange={(e) => u('sourcePort', e.target.value)} placeholder="e.g., 80" />
              </div>
              <div className="form-group-md">
                <label>Destination Port</label>
                <input type="text" value={f.destinationPort} onChange={(e) => u('destinationPort', e.target.value)} placeholder="e.g., 443" />
              </div>
              <div className="form-group-md">
                <label>Priority</label>
                <input type="number" value={f.priority} onChange={(e) => u('priority', parseInt(e.target.value) || 100)} min={1} max={999} />
              </div>
              <div className="form-group-md">
                <label>
                  <input type="checkbox" checked={f.isEnabled} onChange={(e) => u('isEnabled', e.target.checked)} /> Enabled
                </label>
              </div>
              <div className="form-group-md full-width-md">
                <label>Reason / Notes</label>
                <input type="text" value={f.reason} onChange={(e) => u('reason', e.target.value)} placeholder="Why this rule exists (for audit)" />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button onClick={() => setShowRuleModal(false)} className="btn-secondary">Cancel</button>
            <button onClick={saveRule} className="btn-primary" disabled={loading}>{loading ? 'Saving...' : (editingRule ? 'Update Rule' : 'Create & Deploy')}</button>
          </div>
        </div>
      </div>
    );
  };

  // ===== Main Render =====
  return (
    <div className="multi-router-deployment">
      {/* Alerts */}
      {error && <div className="mrd-alert mrd-alert-error"><FaExclamationTriangle /> {sanitizeText(error)}<button onClick={() => setError(null)} className="alert-close">×</button></div>}
      {success && <div className="mrd-alert mrd-alert-success"><FaCheckCircle /> {sanitizeText(success)}<button onClick={() => setSuccess(null)} className="alert-close">×</button></div>}

      {/* Section tabs */}
      <div className="mrd-tabs">
        {sections.map(s => (
          <button key={s.key} className={`mrd-tab ${activeSection === s.key ? 'active' : ''}`} onClick={() => setActiveSection(s.key)}>
            {s.icon} {s.label}{s.count != null ? ` (${s.count})` : ''}
          </button>
        ))}
      </div>

      {/* Active section */}
      <div className="mrd-content">
        {activeSection === 'rules' && renderUnifiedRules()}
        {activeSection === 'deploy' && renderBulkDeploy()}
        {activeSection === 'history' && renderHistory()}
      </div>

      {renderRuleModal()}
    </div>
  );
};

export default MultiRouterDeployment;
